import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { identitySwitchPortal } from '@/store/identitySlice';
import { PORTAL_BRANDING, portalCodeToPath } from '@/config/portalConfig';
import type { PortalCode } from '@/types/identity';
import { showApiError } from '@/utils/apiError';

export function PortalHubPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector((s) => s.identity.session);

  if (!session) {
    return null;
  }

  const uniquePortals = session.availablePortals.reduce(
    (acc, p) => {
      const key = `${p.profileId}-${p.portalCode}`;
      if (!acc.some((x) => `${x.profileId}-${x.portalCode}` === key)) {
        acc.push(p);
      }
      return acc;
    },
    [] as typeof session.availablePortals,
  );

  const handleSelect = async (profileId: number, portalCode: PortalCode) => {
    try {
      await dispatch(identitySwitchPortal({ profileId, portalCode })).unwrap();
      navigate(portalCodeToPath(portalCode), { replace: true });
    } catch (err) {
      showApiError(err, 'Could not open portal');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Choose a portal</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Signed in as {session.email}. Select where you want to go.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {uniquePortals.map((portal) => {
            const branding = PORTAL_BRANDING[portal.portalCode as PortalCode];
            return (
              <button
                key={`${portal.profileId}-${portal.portalCode}`}
                type="button"
                onClick={() => handleSelect(portal.profileId, portal.portalCode as PortalCode)}
                className="card p-5 text-left hover:ring-2 hover:ring-primary-500 transition-shadow"
              >
                <div className={`inline-block rounded-lg px-2 py-1 text-xs font-medium text-white bg-gradient-to-r ${branding.accentClass}`}>
                  {portal.portalCode}
                </div>
                <h2 className="font-semibold text-slate-900 dark:text-slate-100 mt-3">
                  {branding.title}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {portal.displayName || portal.organizationName}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
